import React, { useState, useEffect } from "react";
import {
  Modal,
  StyleSheet,
  TouchableOpacity,
  View,
  Alert,
} from "react-native";
import { IconButton, Text, useTheme } from "react-native-paper";
import { Image } from "expo-image";
import { useTranslation } from "react-i18next";
import {
  MediaViewerProps,
  isImage,
  isVideo,
  isAudio,
  validateWebVideoSource,
  logMediaInfo,
  getVideoErrorMessage,
  logVideoError,
  MEDIA_STYLES,
} from "../utils/mediaUtils";

const MediaViewer = ({ visible, onClose, media }: MediaViewerProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);

  // Reset video error when media changes
  useEffect(() => {
    setVideoError(null);

    // Log media information for debugging
    logMediaInfo(media, 'web');
  }, [media.uri, media.mimeType]);


  const handleShare = async () => {
    try {
      setIsLoading(true);
      
      // Web sharing using Web Share API or download
      if (navigator.share) {
        try {
          await navigator.share({
            title: media.name || t("Media"),
            url: media.uri,
          });
        } catch (error) {
          // Fallback to download
          const link = document.createElement('a');
          link.href = media.uri;
          link.download = media.name || 'media';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        // Fallback to download
        const link = document.createElement('a');
        link.href = media.uri;
        link.download = media.name || 'media';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Error sharing:", error);
      Alert.alert(t("Error"), t("Failed to share file"));
    } finally {
      setIsLoading(false);
    }
  };

  const renderVideoElement = () => {
    const validation = validateWebVideoSource(media.uri, media.mimeType);
    if (!validation.isValid) {
      return (
        <View style={styles.errorContainer}>
          <IconButton
            icon="alert-circle"
            size={64}
            iconColor={theme.colors.error}
          />
          <Text style={styles.errorText}>{t("Video source error")}</Text>
          <Text style={styles.errorSubtext}>{validation.error}</Text>
        </View>
      );
    }

    return (
      <video
        style={styles.webVideo}
        controls
        autoPlay={false}
        preload="metadata"
        onError={(e) => {
          const videoElement = e.target as HTMLVideoElement;
          const error = videoElement.error;
          const errorMessage = getVideoErrorMessage(error);

          logVideoError(error, media.uri, media.mimeType);
          setVideoError(errorMessage);
        }}
        onLoadStart={() => {
          console.log('Video loading started:', media.uri);
          setVideoError(null);
        }}
        onCanPlay={() => {
          console.log('Video can play:', media.uri);
          setVideoError(null);
        }}
      >
        <source src={media.uri} type={media.mimeType || "video/mp4"} />
        {/* Fallback sources for common video formats */}
        {media.mimeType !== "video/mp4" && (
          <source src={media.uri} type="video/mp4" />
        )}
        <source src={media.uri} type="video/webm" />
        <source src={media.uri} type="video/ogg" />
        Your browser does not support the video tag.
      </video>
    );
  };

  const renderAudioElement = () => {
    return (
      <View style={styles.audioContainer}>
        <audio
          style={styles.webAudio}
          controls
          preload="metadata"
          onError={(e) => {
            console.error('Audio error:', e);
            setVideoError('Failed to load audio');
          }}
        >
          <source src={media.uri} type={media.mimeType || "audio/mpeg"} />
          Your browser does not support the audio element.
        </audio>
        <Text style={styles.audioText}>{media.name || t("Audio File")}</Text>
      </View>
    );
  };

  const renderMediaContent = () => {
    if (isImage(media.mimeType)) {
      return (
        <Image
          source={{ uri: media.uri }}
          style={styles.image}
          contentFit="contain"
          transition={200}
        />
      );
    }

    if (isVideo(media.mimeType)) {
      if (videoError) {
        return (
          <View style={styles.errorContainer}>
            <IconButton
              icon="alert-circle"
              size={64}
              iconColor={theme.colors.error}
            />
            <Text style={styles.errorText}>{t("Video playback error")}</Text>
            <Text style={styles.errorSubtext}>{videoError}</Text>
          </View>
        );
      }

      return renderVideoElement();
    }

    if (isAudio(media.mimeType)) {
      if (videoError) {
        return (
          <View style={styles.errorContainer}>
            <IconButton
              icon="alert-circle"
              size={64}
              iconColor={theme.colors.error}
            />
            <Text style={styles.errorText}>{t("Audio playback error")}</Text>
            <Text style={styles.errorSubtext}>{videoError}</Text>
          </View>
        );
      }

      return renderAudioElement();
    }

    // Default fallback for unknown file types
    return (
      <View style={styles.unknownContainer}>
        <IconButton
          icon="file"
          size={64}
          iconColor={theme.colors.primary}
        />
        <Text style={styles.unknownText}>
          {media.name || t("File")}
        </Text>
        <Text style={styles.unknownSubtext}>
          {media.mimeType || t("Unknown file type")}
        </Text>
      </View>
    );
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <TouchableOpacity
          style={styles.overlayTouchable}
          activeOpacity={1}
          onPress={onClose}
        >
          <View style={styles.container}>
            <View style={styles.header}>
              <Text style={styles.title} numberOfLines={1}>
                {media.name || t("Media")}
              </Text>
              <View style={styles.headerActions}>
                <IconButton
                  icon="share"
                  size={24}
                  iconColor={theme.colors.onSurface}
                  onPress={handleShare}
                  disabled={isLoading}
                />
                <IconButton
                  icon="close"
                  size={24}
                  iconColor={theme.colors.onSurface}
                  onPress={onClose}
                />
              </View>
            </View>
            <View style={styles.content}>
              {renderMediaContent()}
            </View>
          </View>
        </TouchableOpacity>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: MEDIA_STYLES.OVERLAY,
  overlayTouchable: MEDIA_STYLES.OVERLAY_TOUCHABLE,
  container: MEDIA_STYLES.CONTAINER,
  header: MEDIA_STYLES.HEADER,
  title: MEDIA_STYLES.TITLE,
  headerActions: MEDIA_STYLES.HEADER_ACTIONS,
  content: MEDIA_STYLES.CONTENT,
  image: MEDIA_STYLES.IMAGE,
  webVideo: MEDIA_STYLES.WEB_VIDEO,
  webAudio: MEDIA_STYLES.WEB_AUDIO,
  audioContainer: MEDIA_STYLES.AUDIO_CONTAINER,
  audioText: MEDIA_STYLES.AUDIO_TEXT,
  unknownContainer: MEDIA_STYLES.UNKNOWN_CONTAINER,
  unknownText: MEDIA_STYLES.UNKNOWN_TEXT,
  unknownSubtext: MEDIA_STYLES.UNKNOWN_SUBTEXT,
  errorContainer: MEDIA_STYLES.ERROR_CONTAINER,
  errorText: MEDIA_STYLES.ERROR_TEXT,
  errorSubtext: MEDIA_STYLES.ERROR_SUBTEXT,
});

export default MediaViewer;
